import axios, {
  AxiosError,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';
import type { BaseQueryFn } from '@reduxjs/toolkit/query';

import { PORTAL_AUTH_CODE_STORAGE_KEY } from '../../portal/constants';

import { env } from './env';
import { logRocketSessionURL } from './logrocket';
import { mixpanelService } from './mixpanel';

const baseConfig = {
  withCredentials: true,
  baseURL: env.baseApiUrl,
};

export const orgHeader = `X-Organisation-Id`;

export const baseRequest = axios.create(baseConfig);

// CSRF: the app API requires a session bound token on state changing requests
// (see backend `csrfProtection`). Fetched once per page load and cached.
const CSRF_HEADER = `x-csrf-token`;
// Marks the retry so the server can tell a stale cached token from a real mismatch.
const CSRF_RETRY_HEADER = `x-csrf-retry`;
const CSRF_SAFE_METHODS = [`get`, `head`, `options`];
let csrfTokenRequest: Promise<string> | undefined;

async function attachCsrfToken(config: InternalAxiosRequestConfig) {
  const method = (config.method || `get`).toLowerCase();

  if (config.baseURL === env.baseApiUrl && !CSRF_SAFE_METHODS.includes(method)) {
    csrfTokenRequest ??= baseRequest.get<{ csrfToken: string }>(`csrf-token`).then(res => res.data.csrfToken);

    try {
      config.headers.set(CSRF_HEADER, await csrfTokenRequest);
    }
    catch (err) {
      csrfTokenRequest = undefined;
      throw err;
    }
  }

  return config;
}

// The token can be lost when concurrent requests overwrite the session, so
// refetch it and retry once.
async function retryOnCsrfMismatch(err: AxiosError) {
  const config = err.config as (InternalAxiosRequestConfig & { csrfRetried?: boolean }) | undefined;

  if (config && !config.csrfRetried && err.response?.status === 403 && err.response.data === `Invalid CSRF token`) {
    csrfTokenRequest = undefined;
    config.csrfRetried = true;
    config.headers.set(CSRF_RETRY_HEADER, `1`);

    return axios(config);
  }

  throw err;
}

// Backend requestLog middleware stamps this onto every log line of the request.
const LOGROCKET_HEADER = `x-logrocket-session`;

function attachLogRocketSession(config: InternalAxiosRequestConfig) {
  if (logRocketSessionURL && [env.baseApiUrl, env.basePortalApiUrl].includes(config.baseURL)) {
    config.headers.set(LOGROCKET_HEADER, logRocketSessionURL);
  }

  return config;
}

for (const instance of [axios, baseRequest]) {
  instance.interceptors.request.use(attachCsrfToken);
  instance.interceptors.request.use(attachLogRocketSession);
  instance.interceptors.response.use(undefined, retryOnCsrfMismatch);
}

export interface ErrorPayload {
    status: number;
    data: {
      message: string;
    };
    /** Seconds until a rate limited (429) request can be retried */
    retryAfterSeconds?: number;
}

export interface ErrorResponse {
  error: ErrorPayload;
}

// https://redux-toolkit.js.org/rtk-query/usage-with-typescript
export const axiosBaseQuery = (baseUrl: string): BaseQueryFn<
  AxiosRequestConfig,
  unknown,
  unknown
  > => async(requestOpts, { signal }) => {
  try {
    // Always append the org
    const axiosConfig = {
      ...requestOpts,
      ...baseConfig,
      baseURL: baseUrl,
      // Let RTK Query cancel the request when it aborts the query
      signal,
    };

    if (baseUrl === env.basePortalApiUrl && typeof sessionStorage !== `undefined`) {
      const portalAuthCode = sessionStorage.getItem(PORTAL_AUTH_CODE_STORAGE_KEY);

      if (portalAuthCode) {
        axiosConfig.headers = {
          ...(axiosConfig.headers || {}),
          [`X-Portal-Auth-Code`]: portalAuthCode,
        };
      }
    }

    const result = await axios(axiosConfig);

    return {
      data: result.data,
      meta: {
        headers: result.headers,
        method: (result.config.method || `get`).toUpperCase(),
      },
    };
  }
  catch (axiosError: unknown) {
    const err = axiosError as AxiosError;

    const retryAfterSeconds = Number(err.response?.headers?.[`retry-after`]);

    const errorRes: ErrorResponse = {
      error: {
        status: err.response?.status,
        data: {
          message: err.response?.data as string || err.message,
        },
        ...(Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0 ? { retryAfterSeconds } : {}),
      },
    };

    if (baseUrl === env.baseApiUrl) {
      // In app , do login redirect
      if (errorRes.error.status === 401 && requestOpts.method === `GET`) {
        // Reset the mixpanel client
        mixpanelService.logout();
        // REDIRECT TO LOGIN
        window.location.href = `${env.apiUrl}/login?redirect=${window.location.href}`;
      }
    }

    return errorRes;
  }
};
